import { useEffect, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { Link } from "react-router-dom";
import { type Creche, formatAdresseComplete } from "../data/creches";

type CrecheMapProps = {
  creches: Creche[];
  highlightedSlug?: string;
  className?: string;
};

const iconCache = new Map<boolean, L.DivIcon>();
const getIcon = (highlighted: boolean) => {
  let icon = iconCache.get(highlighted);
  if (!icon) {
    icon = buildIcon(highlighted);
    iconCache.set(highlighted, icon);
  }
  return icon;
};

const buildIcon = (highlighted: boolean) =>
  L.divIcon({
    className: "lalaland-marker",
    html: `<div style="
      width: ${highlighted ? 42 : 30}px;
      height: ${highlighted ? 42 : 30}px;
      background: ${highlighted ? "#4a9b8b" : "#ef9d56"};
      transition: all .2s ease;
      border: 3px solid #fbf7f0;
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      box-shadow: 0 4px 12px rgba(31, 27, 22, 0.18);
      display: flex; align-items: center; justify-content: center;
    "><span style="
      transform: rotate(45deg);
      color: #fbf7f0;
      font-weight: 700;
      font-size: ${highlighted ? 16 : 12}px;
      font-family: Inter, system-ui, sans-serif;
    ">L</span></div>`,
    iconSize: [highlighted ? 42 : 30, highlighted ? 42 : 30],
    iconAnchor: [highlighted ? 21 : 15, highlighted ? 42 : 30],
    popupAnchor: [0, highlighted ? -38 : -28],
  });

function FitBounds({ creches }: { creches: Creche[] }) {
  const map = useMap();
  useEffect(() => {
    if (creches.length === 0) return;
    if (creches.length === 1) {
      const { lat, lng } = creches[0].coordonnees;
      map.setView([lat, lng], 13);
      return;
    }
    const bounds = L.latLngBounds(
      creches.map((c) => [c.coordonnees.lat, c.coordonnees.lng] as [number, number]),
    );
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [creches, map]);
  return null;
}

export default function CrecheMap({
  creches,
  highlightedSlug,
  className = "",
}: CrecheMapProps) {
  const mapRef = useRef<L.Map | null>(null);
  const center = useMemo<[number, number]>(() => [49.44, 1.1], []);

  return (
    <div
      className={`relative isolate z-0 rounded-3xl overflow-hidden border border-edge-soft shadow-soft-md ${className}`}
    >
      <MapContainer
        center={center}
        zoom={10}
        scrollWheelZoom={false}
        className="h-[360px] md:h-[460px] lg:h-[calc(100vh-9rem)] lg:max-h-[720px] w-full"
        ref={(m) => {
          mapRef.current = m;
        }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds creches={creches} />
        {creches.map((c) => {
          const highlighted = c.slug === highlightedSlug;
          return (
            <Marker
              key={c.slug}
              position={[c.coordonnees.lat, c.coordonnees.lng]}
              icon={getIcon(highlighted)}
              zIndexOffset={highlighted ? 1000 : 0}
            >
              <Popup>
                <div className="font-sans" style={{ minWidth: 220 }}>
                  <p
                    className="font-display"
                    style={{
                      fontFamily: "Fraunces, Georgia, serif",
                      fontSize: "1.05rem",
                      fontWeight: 600,
                      color: "#1f1b16",
                      letterSpacing: "-0.01em",
                      margin: 0,
                    }}
                  >
                    {c.nom}
                  </p>
                  <p
                    style={{
                      fontSize: "0.8rem",
                      color: "#6b6258",
                      margin: "4px 0 8px",
                    }}
                  >
                    {formatAdresseComplete(c)}
                  </p>
                  <Link
                    to={`/micro-creches/${c.slug}`}
                    style={{
                      display: "inline-block",
                      background: "#ef9d56",
                      color: "#fbf7f0",
                      padding: "6px 14px",
                      borderRadius: "999px",
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      textDecoration: "none",
                    }}
                  >
                    Voir la crèche →
                  </Link>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
}
